import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw, ShieldCheck } from 'lucide-react';
import { apiBase } from '../utils/authHeaders';
import { commandId } from './CkOperationsShared';
import { persistGrantRetry, loadGrantRetry, clearGrantRetry } from './operandiGrantQueue';

const grantCommandId = () => {
  const hex = commandId().slice(3);
  return `${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20)}`;
};

export default function OperandiAdvisoryGrants({ headers, scope }) {
  const [config,setConfig] = useState(null), [user,setUser] = useState('');
  const [enabled,setEnabled] = useState(false), [reason,setReason] = useState('');
  const [confirmed,setConfirmed] = useState(false), [pending,setPending] = useState(null);
  const [busy,setBusy] = useState(false), [error,setError] = useState(''), [notice,setNotice] = useState('');
  const abort = useRef(null), lock = useRef(false), alive = useRef(true);
  useEffect(() => {alive.current = true;return () => {alive.current = false;abort.current?.abort();};},[]);
  const request = async (method,body) => {
    abort.current?.abort(); const controller = new AbortController(); abort.current = controller;
    const response = await fetch(`${apiBase()}/api/operandi/advisory-grants`,{method,headers:{...headers,'Content-Type':'application/json'},
      signal:controller.signal,...(body ? {body:JSON.stringify(body)} : {})});
    const result = await response.json();
    if (controller.signal.aborted) throw new Error('Scope changed');
    if (!response.ok) throw new Error(result.detail || 'Advisory grant unavailable');
    return result;
  };
  const load = async () => {
    if(lock.current || pending) return; lock.current = true; setBusy(true); setError(''); setConfig(null); setNotice('');
    try {
      const saved = await loadGrantRetry(scope);
      if (!alive.current || abort.current?.signal.aborted) return;
      if (saved) {
        setConfig(saved.config);setPending(saved.request);setUser(saved.request.user_id);
        setEnabled(saved.request.enabled);setReason(saved.request.reason);setConfirmed(false);
        setNotice('Pending grant response unconfirmed. Retry the original command.');
        return;
      }
      const result = await request('GET');
      if(result.authority !== 'ADVISORY_ONLY' || result.no_action_authority !== true ||
        !Array.isArray(result.user_ids) || !Array.isArray(result.eligible_user_ids) ||
        !Array.isArray(result.site_ids) || !Array.isArray(result.entity_ids) || !Number.isInteger(result.grants_version))
        throw new Error('Invalid advisory grant configuration');
      setConfig(result); setUser(''); setConfirmed(false);
    } catch(failure) {if(!abort.current?.signal.aborted) setError(failure.message);}
    finally {lock.current = false;if(!abort.current?.signal.aborted) setBusy(false);}
  };
  const save = async () => {
    if(lock.current || !config || (!pending && (!user || !reason.trim() || !confirmed))) return;
    const body = pending || {command_id:grantCommandId(),binding_id:config.binding_id,binding_version:config.binding_version,
      expected_grants_version:config.grants_version,expected_site_ids:config.site_ids,expected_entity_ids:config.entity_ids,
      user_id:user,enabled,reason:reason.trim()};
    setPending(body); lock.current = true; setBusy(true); setError('');
    try {
      const storedKey = await persistGrantRetry(scope,body,config);
      if (!alive.current || abort.current?.signal.aborted) return;
      const result = await request('POST',body);
      if(result.binding_id !== body.binding_id || result.binding_version !== body.binding_version ||
        result.user_id !== body.user_id || result.enabled !== body.enabled || result.grants_version !== body.expected_grants_version+1 ||
        result.authority !== 'ADVISORY_ONLY' || result.no_action_authority !== true) throw new Error('Grant acknowledgement unconfirmed');
      clearGrantRetry(storedKey);
      setConfig(current => ({...current,grants_version:result.grants_version,user_ids:result.enabled ?
        [...new Set([...current.user_ids,result.user_id])] : current.user_ids.filter(id => id !== result.user_id)}));
      setPending(null);setConfirmed(false);setNotice('Advisory grant recorded. No action authority.');
    } catch(failure) {if(!abort.current?.signal.aborted) setError(failure.message);}
    finally {lock.current = false;if(!abort.current?.signal.aborted) setBusy(false);}
  };
  return <section className="mt-5 border-t border-gray-200 pt-4" aria-label="Owner advisory grants">
    <h2 className="font-semibold">Owner advisory grants</h2>
    <button disabled={busy || !!pending} onClick={load} className="min-h-11 inline-flex gap-2 items-center"><RefreshCw size={16}/>Load advisory grants</button>
    {error && <p role="alert">{error}</p>}{notice && <p role="status">{notice}</p>}
    {config && <div className="space-y-2 text-sm">
      <p className="break-words">Binding: {config.binding_id}; version: {config.binding_version}; grants version: {config.grants_version}.</p>
      <p className="break-words">Sites: {config.site_ids.join(', ')}. Entities: {config.entity_ids.join(', ')}.</p>
      <p className="break-words">Granted users: {config.user_ids.join(', ') || 'None'}.</p>
      <label className="block">User<select value={user} disabled={busy || !!pending} onChange={event => {setUser(event.target.value);setConfirmed(false);}} className="block max-w-full min-h-11">
        <option value="">Select user</option>{[...new Set([...config.eligible_user_ids,...config.user_ids])].map(id => <option key={id} value={id}>{id}</option>)}
      </select></label>
      {config.eligible_users_truncated && <p>First 100 eligible users shown.</p>}
      <label className="block"><input type="checkbox" disabled={busy || !!pending} checked={enabled} onChange={event => {setEnabled(event.target.checked);setConfirmed(false);}}/> Enable advisory analysis</label>
      <label className="block">Reason<input className="block w-full min-h-11" maxLength={500} value={reason} disabled={busy || !!pending} onChange={event => setReason(event.target.value)}/></label>
      <label className="block"><input type="checkbox" disabled={busy || !!pending} checked={confirmed} onChange={event => setConfirmed(event.target.checked)}/> Confirm advisory-only change across the listed binding sites and entities</label>
      <button onClick={save} disabled={busy || (!pending && (!user || !reason.trim() || !confirmed))} className="min-h-11 inline-flex gap-2 items-center"><ShieldCheck size={16}/>{pending ? 'Retry exact grant change' : 'Record grant change'}</button>
    </div>}
  </section>;
}
